import { useState, useEffect, MouseEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'hooks/redux';
import { logout } from 'store/auth/auth-slice';
import { Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../select/language-select';
import { ReactComponent as ArrowDownSvg } from 'assets/svg/arrow-down.svg';
import { ReactComponent as PawSvg } from 'assets/svg/paw.svg';
import { ReactComponent as CheckoutSvg } from 'assets/svg/checkout.svg';
import { ReactComponent as BellSvg } from 'assets/svg/bell.svg';
import { ReactComponent as UserOutlineSvg } from 'assets/svg/user-outline.svg';
import { ReactComponent as LogoutSvg } from 'assets/svg/logout.svg';
import { CMenuItem } from 'components/atoms';
import { usePetOwnerDetailQuery } from 'services/pet-owner';
import RightDrawer from '../drawer/right-drawer';
import Notifications from 'components/pages/dashboard/notifications';
import customTwMerge from 'utils/twMerge';
import { useBasketItemsQuery } from 'services/basket';
import { useNotificationsQuery } from 'services/notification';
import UpdatePaymentMethod from 'components/pages/payment/update-payment-method';

export default function AccountMenu() {
  const { t } = useTranslation('common');
  const initialDrawerState: {
    isOpenType: 'notifications' | 'updateCard' | null;
    data?: any;
  } = {
    isOpenType: null,
    data: null,
  };
  const [drawer, setDrawer] = useState(initialDrawerState);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleClose();
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    handleClose();
  };

  const openUpdateCardDrawer = (membership: {
    patientId?: number;
    subscriptionGroupId?: number;
    subscriptionId?: number;
    patientSubscriptionId?: number;
    cancellationId?: number;
  }) => setDrawer({ isOpenType: 'updateCard', data: membership });

  const closeDrawer = () => setDrawer({ isOpenType: null, data: null });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user]);

  const { data } = usePetOwnerDetailQuery();
  const { data: basketItemData } = useBasketItemsQuery(
    {
      data: {
        productId: null,
        productType: null,
      },
    },
    { refetchOnFocus: true }
  );

  const { data: notificationItems } = useNotificationsQuery(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    {},
    { refetchOnMountOrArgChange: true }
  );

  return (
    <>
      <div className="ml-auto flex items-center md:gap-8">
        <div className="hidden items-center gap-8 md:flex">
          <NavLink
            to="/pet-mates"
            className={({ isActive, isPending }) =>
              customTwMerge(
                `hover-transition text-neutral-500 hover:fill-primary-200 hover:text-neutral-700 ${
                  isActive ? 'fill-primary-200 text-neutral-700' : 'fill-transparent'
                }`
              )
            }
          >
            <Tooltip title={t('my-pet-mates')}>
              <span>
                <PawSvg className="h-8 w-8 fill-inherit text-inherit" />
              </span>
            </Tooltip>
          </NavLink>

          <NavLink
            to="/checkout"
            className={({ isActive, isPending }) =>
              customTwMerge(
                `hover-transition text-neutral-500 hover:fill-primary-200 hover:text-neutral-700 ${
                  isActive ? 'fill-primary-200 text-neutral-700' : 'fill-transparent'
                }`
              )
            }
          >
            <Tooltip title={t('basket')}>
              <Badge
                showZero
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#002F87',
                  },
                }}
                color="primary"
                badgeContent={basketItemData?.data?.basketItems?.length || 0}
                overlap="circular"
                // variant={basketItemData?.data?.basketItems?.length > 0 && 'dot'}
              >
                <CheckoutSvg className="h-8 w-8 fill-inherit text-inherit" />
              </Badge>
            </Tooltip>
          </NavLink>

          <div
            className="hover-transition"
            onClick={() => setDrawer({ ...drawer, isOpenType: 'notifications' })}
          >
            <Tooltip title={t('notifications')}>
              <Badge
                showZero
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#002F87',
                  },
                }}
                // color="primary"
                // badgeContent={2}
                overlap="circular"
                variant={
                  notificationItems?.data.filter((notification) => notification.status === 1).length >
                    0 && 'dot'
                }
              >
                <BellSvg className="h-8 w-8 text-neutral-500 hover:fill-primary-200" />
              </Badge>
            </Tooltip>
          </div>
        </div>

        <div>
          <Tooltip title={t('my-account')}>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              className="hover-transition gap-2 p-0"
              disableRipple
            >
              <Avatar
                data-sentry-mask
                classes={{ root: 'flex cursor-pointer items-center justify-center bg-neutral-200' }}
                src={data?.data.photo}
                alt={data?.data.fullName}
              />

              <ArrowDownSvg className={`transition-transform  ${open ? 'rotate-180' : ''}`} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <div className="md:hidden">
          <NavLink
            to="/pet-mates"
            className={({ isActive, isPending }) =>
              customTwMerge(
                `hover-transition text-neutral-500 hover:fill-primary-200 hover:text-neutral-700 ${
                  isActive ? 'bg-primary-100 fill-primary-200 text-neutral-700' : 'fill-transparent'
                }`
              )
            }
          >
            <CMenuItem
              onClick={() => handleNavigate('/pet-mates')}
              className="bg-inherit !text-inherit"
            >
              <ListItemIcon className="text-inherit">
                <PawSvg className="h-6 w-6 fill-inherit" />
              </ListItemIcon>
              {t('my-pet-mates')}
            </CMenuItem>
          </NavLink>

          <NavLink
            to="/checkout"
            className={({ isActive, isPending }) =>
              customTwMerge(
                `hover-transition text-neutral-500 hover:fill-primary-200 hover:text-neutral-700 ${
                  isActive ? 'bg-primary-100 fill-primary-200 text-neutral-700' : 'fill-transparent'
                }`
              )
            }
          >
            <CMenuItem onClick={() => handleNavigate('/checkout')} className="bg-inherit !text-inherit">
              <ListItemIcon className="text-inherit">
                <CheckoutSvg className="h-6 w-6 fill-inherit" />
              </ListItemIcon>
              {t('basket')}
            </CMenuItem>
          </NavLink>

          <CMenuItem
            onClick={() => setDrawer({ ...drawer, isOpen: true })}
            className="fill-transparent hover:fill-primary-200"
          >
            <ListItemIcon className="text-inherit">
              <BellSvg className="h-6 w-6 fill-inherit" />
            </ListItemIcon>
            {t('notifications')}
          </CMenuItem>

          <Divider className="!my-1" />
        </div>

        <NavLink
          to="/account"
          className={({ isActive, isPending }) =>
            customTwMerge(
              `hover-transition text-neutral-500 hover:fill-primary-200 hover:text-neutral-700 ${
                isActive ? 'bg-primary-100 fill-primary-200 text-neutral-700' : 'fill-transparent'
              }`
            )
          }
        >
          <CMenuItem onClick={() => handleNavigate('/account')} className="bg-inherit !text-inherit">
            <ListItemIcon className="text-inherit">
              <UserOutlineSvg className="h-6 w-6 fill-inherit" />
            </ListItemIcon>
            {t('my-account')}
          </CMenuItem>
        </NavLink>

        <LanguageSelect type="click" />

        <Divider className="!my-1" />

        <CMenuItem onClick={handleLogout} className="fill-transparent hover:fill-primary-200">
          <ListItemIcon className="text-inherit">
            <LogoutSvg className="h-6 w-6 fill-inherit" />
          </ListItemIcon>
          {t('logout')}
        </CMenuItem>
      </Menu>

      <RightDrawer
        isOpen={drawer.isOpenType === 'notifications'}
        onClose={closeDrawer}
        title={t('notifications')}
        content={
          <Notifications
            closeDrawer={() => setDrawer(initialDrawerState)}
            openUpdateCardDrawer={openUpdateCardDrawer}
          />
        }
        contentClassName="p-0 overflow-hidden"
      />
      <RightDrawer
        isOpen={drawer.isOpenType === 'updateCard'}
        onClose={closeDrawer}
        title={t('update-payment-method')}
        content={<UpdatePaymentMethod membership={drawer.data} />}
      />
    </>
  );
}

